import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { CardStatus } from '@bciti/types';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { BcitiContextService } from 'src/app/global/services/context/context.abstract.service';
import { LanguageConfiguration, OrganisationConfiguration } from 'src/app/global/global.model';
import { languageSelector } from 'src/app/global/store/global.selectors';
import { UserService } from '../../service/user.abstract.service';
import { CardActions, FamilyLink, PatchApproveRefuseFamilyLink, ProfileResponse, UserRecordTransformedUser } from '../../user.model';
import { StepMembersComponent } from './steps/step-members/step-members.component';
import { UserCardRequestFormData } from '../../forms/user-card-request-detail-form/user-card-request-detail-form.component';
import { UserUtilService } from '../../util/user-util.service';
import { UserRequestDenyDialogComponent } from '../user-request-deny-dialog/user-request-deny-dialog.component';

@Component({
    selector: 'app-user-family-and-card-dialog',
    templateUrl: './user-family-and-card-dialog.component.html',
    styleUrls: ['./user-family-and-card-dialog.component.scss'],
})
export class UserFamilyAndCardDialogComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    @ViewChild(StepMembersComponent)
    public stepMembers: StepMembersComponent;

    public currentMember: any;

    public familyLinks: FamilyLink[];

    public cardFormData: UserCardRequestFormData;

    // Stores User A's (the selected user) pending card data separately
    public userACardFormData: UserCardRequestFormData;

    public profile: ProfileResponse;

    orgLang: LanguageConfiguration;
    orgConfig: OrganisationConfiguration;

    currentLang: string;

    public loading = false;

    processingSubmit = false;

    approvalGuidelines: string;
    showApprovalGuide = false;

    public stepIndex = 0;

    public steps = [
        this.translate.instant('pages.userFamily.stepPickFamilyMember.shortHeading'),
        this.translate.instant('pages.userFamily.stepConfirmFamilyMember.shortHeading'),
        this.translate.instant('pages.userFamily.uploaded.label'),
    ]; // Used for descriptive purposes. Only the index is tracked here.

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { user: UserRecordTransformedUser },
        @Inject('BCITI_CONTEXT_SERVICE') private contextService: BcitiContextService,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<UserFamilyAndCardDialogComponent>,
        private translate: TranslateService,
        public userService: UserService,
        private messageService: MessageService,
        private userUtil: UserUtilService,
        private store: Store<State>,
    ) {}

    ngOnInit(): void {
        this.loading = true;

        combineLatest([this.contextService.getOrganisationConfiguration(), this.store.select(languageSelector)])
            .pipe(
                map(([orgConfig, lang]) => {
                    this.orgLang = orgConfig.lang;
                    this.currentLang = lang;
                    this.orgConfig = orgConfig;
                    this.approvalGuidelines = orgConfig?.family?.approvalGuidelines?.[lang] || '';
                }),
            )
            .subscribe();

        this.userService.getProfileById(this.data.user.id).subscribe((profile) => {
            this.profile = profile;
        });
        this.userService.getUserFamily(this.data.user.id).subscribe((links) => {
            this.familyLinks = links;
            this.loading = false;
        });

        this.storeUserACardData(this.data.user);
    }

    public currentMemberChanged(selectedFamilyMember: FamilyLink): void {
        this.currentMember = selectedFamilyMember;
    }

    public submitClicked(index): void {
        switch (index) {
            case 0: // Step pick member
                this.stepMembers.highlightWarnings = true;
                break;
            default:
                break;
        }
    }

    public closeDialog(v?: boolean | { success: boolean; openCardDialogForUserA?: UserCardRequestFormData }): void {
        this.dialogRef.close(v);
    }

    /**
     * Get the active step index for the stepper progress indicator.
     * When showing the card form (stepIndex >= 2 and cardFormData exists),
     * return the card step index (last step) instead of stepIndex.
     */
    public getActiveStepIndex(): number {
        if (this.stepIndex >= 2 && this.cardFormData) {
            // When showing card form, highlight the card step (last step in the array)
            return this.steps.length - 1;
        }
        return this.stepIndex;
    }

    public next(event?): void {
        event?.stopPropagation();
        // if first step and the link request with currentMember has been made by the source
        if (this.stepIndex === 0 && this.currentMember.isSource) {
            this.currentMember.userId.cards = this.currentMember.userId?.wallets?.[0]?.cards;
            this.addPrimaryCardStep(this.userUtil.getTransformedUser(this.currentMember.userId));
        }
        this.stepIndex += 1;

        // Close dialog if we're at step 2 and there's no card to process
        if (this.stepIndex === 2 && !this.cardFormData) {
            this.closeDialog(true);
        }
    }

    public back(): void {
        if (this.stepIndex === 1) {
            // if > 2 steps, means we added the card approval step.
            // we must reset steps to the first two elements only
            if (this.steps.length > 2 && this.currentMember.isSource) {
                this.steps = this.steps.slice(0, 2);
            }
            this.currentMember = null;
        }
        this.stepIndex -= 1;
    }

    public submit(confirmed: boolean): void {
        this.processingSubmit = true;
        const payload: PatchApproveRefuseFamilyLink = {
            confirmed,
            id: this.profile._id,
            family_member_id: this.currentMember.userId._id,
        };
        if (!confirmed) {
            this.closeDialog(true);
            let langLabel = this.translate.instant(`common.langs.${this.orgLang.default}`);
            if (this.data.user.preferences?.language && this.orgLang.available.includes(this.data.user.preferences?.language)) {
                langLabel = this.translate.instant(`common.langs.${this.data.user.preferences.language}`);
            }
            const locale = this.currentLang || this.orgLang.default;
            const denialReasons = (this.orgConfig?.family as any)?.denialReasons?.[locale] || [];
            const dialog = this.dialog.open(UserRequestDenyDialogComponent, {
                autoFocus: false,
                width: '680px',
                panelClass: '',
                data: {
                    title: this.translate.instant('pages.userFamily.denyFamilyLinkDialog.header'),
                    body: {
                        label: this.translate.instant('pages.userFamily.denyFamilyLinkDialog.label', { lang: langLabel }),
                        placeholder: this.translate.instant('pages.userFamily.denyFamilyLinkDialog.placeholder'),
                        error: this.translate.instant('pages.userFamily.denyFamilyLinkDialog.form.errors.reason'),
                        button: this.translate.instant('pages.userFamily.denyFamilyLinkDialog.button.submit'),
                        presetTab: this.translate.instant('pages.userFamily.denyFamilyLinkDialog.presetTab'),
                        customTab: this.translate.instant('pages.userFamily.denyFamilyLinkDialog.customTab'),
                    },
                    denialReasons,
                },
            });
            dialog.componentInstance.onSubmit.subscribe((reason) => {
                dialog.componentInstance.dialogRef.close();
                this.submitApproveRefuseFamilyLink({ ...payload, reason });
            });
        } else {
            this.submitApproveRefuseFamilyLink(payload);
        }
    }

    private submitApproveRefuseFamilyLink(payload): void {
        this.userService
            .patchApproveRefuseFamilyLink(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (_response) => {
                    if (this.stepIndex === this.steps.length - 1) {
                        // Family approved and we're on last step without card step for User B
                        // Check if User A has a pending card request
                        if (payload.confirmed && this.userACardFormData) {
                            this.closeDialog({ success: true, openCardDialogForUserA: this.userACardFormData });
                        } else {
                            this.closeDialog(true);
                        }
                    }
                    this.processingSubmit = false;
                    if (payload.confirmed) {
                        this.messageService.success(this.translate.instant('pages.userFamily.messages.processed.accepted'));
                        this.next();
                    } else {
                        this.messageService.success(this.translate.instant('pages.userFamily.messages.processed.denied'));
                        this.closeDialog(true);
                    }
                },
                (error) => {
                    this.processingSubmit = false;
                    if (error?.error?.messages) {
                        this.messageService.error(this.translate.instant(error.error.messages[0]));
                    } else {
                        this.messageService.error(this.translate.instant('pages.userFamily.messages.processed.error'));
                    }
                },
            );
    }

    /**
     * Note: User A is the user being approved and User B is the family member being approved
     * Stores User A's card data for potential chaining via toast notification
     * This does NOT add a step to the stepper - User A's card will be shown via toast
     * after family approval, allowing the admin to click to open a separate card dialog.
     * @param user UserRecordTransformedUser
     */
    private storeUserACardData(user: UserRecordTransformedUser): void {
        if (user?.bcitiCard?.status === CardStatus.PROCESSING || user?.citizenCard?.status === CardStatus.PROCESSING || user?.legacyCard?.status === CardStatus.PROCESSING) {
            let targetCard;

            if (user?.bcitiCard?.status === CardStatus.PROCESSING) {
                targetCard = user.bcitiCard;
            }
            if (user?.citizenCard?.status === CardStatus.PROCESSING) {
                targetCard = user.citizenCard;
            }
            if (user?.legacyCard?.status === CardStatus.PROCESSING) {
                targetCard = user.legacyCard;
            }

            this.userACardFormData = {
                user,
                card: targetCard,
                action: CardActions.SUBSCRIBE,
            };
        }
    }

    /**
     * Adds the card approval step for the user passed to the function
     * IF the user has a primary card with a "PROCESSING" status
     * This is used for User B (the family member being approved)
     * @param user UserTranformedUser
     */
    private addPrimaryCardStep(user: UserRecordTransformedUser): void {
        if (user?.bcitiCard?.status === CardStatus.PROCESSING || user?.citizenCard?.status === CardStatus.PROCESSING || user?.legacyCard?.status === CardStatus.PROCESSING) {
            this.steps.push(this.translate.instant('pages.userRecords.card.requestDetailsDialog.header'));

            // set UserCardRequestFormData

            let targetCard;

            if (user?.bcitiCard?.status === CardStatus.PROCESSING) {
                targetCard = user.bcitiCard;
            }
            if (user?.citizenCard?.status === CardStatus.PROCESSING) {
                targetCard = user.citizenCard;
            }
            if (user?.legacyCard?.status === CardStatus.PROCESSING) {
                targetCard = user.legacyCard;
            }

            this.cardFormData = {
                user,
                card: targetCard,
                action: CardActions.SUBSCRIBE,
            };
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
